/**
 * Device-bound session registration for the native bridge (P1-12; server:
 * POST /api/auth/device/register, backend/src/modules/device-sessions).
 *
 * After sign-in the bridge holds a "parent" session JWT. It registers this
 * install as a device session and from then on sends the returned token,
 * which carries `sid`. That gives the bridge:
 *   - typed terminal answers (SESSION_REVOKED, MEMBERSHIP_REMOVED,
 *     USER_DISABLED) handled by services/terminal-auth.ts;
 *   - a session an admin can revoke when the phone is lost;
 *   - the right to mint a tracker credential (POST /api/tracker/token) for
 *     field ingest (services/field-ingest.service.ts).
 *
 * Parent token (server contract, device-sessions.controller.ts): discarded
 * once the bound token is stored, except for a user with more than one
 * tenant, who needs it to register this install in another tenant when they
 * switch (a bound token is pinned to one tenant). It is kept in the Keystore
 * vault, never in plaintext.
 *
 * Registration never blocks the app: until it succeeds (offline at sign-in,
 * an older server) the parent token keeps working, and the next trigger
 * (sign-in, tenant switch, resume, a 403 DEVICE_SESSION_TENANT_MISMATCH)
 * tries again.
 *
 * Device key (W11-BRIDGEKEY): on Android the registration carries `device_key`,
 * the install's cb_device_v1 public key with a proof of possession
 * (services/device-key.ts), exactly as the MAUI app sends it, so the session is
 * hardware-bound (device_key_id set) and the MAUI app later finds the same key.
 * The key is an added protection, never a reason to keep a responder out: no
 * key (web, a device without hardware key storage, Keystore busy) registers
 * unbound, and a server refusal of the key or its proof (DEVICE_KEY_PROOF_INVALID,
 * usually a clock more than 5 minutes off; DEVICE_KEY_INVALID) registers once
 * more without it, as native AuthService.RegisterDeviceAsync does.
 *
 * The refresh credential the server returns is kept in the vault for
 * services/device-session-refresh.ts, which renews the 1-hour bound token (with
 * a device-key proof for a key-bound session).
 */
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { decodeJwtPayload, useAuthStore } from '@/store/auth.store';
import { useTenantStore } from '@/store/tenant.store';
import { getSessionTokenVault } from './auth-token-persistence';
import { getNativeDeviceKeySigner, signRegistration, type DeviceKeySigner } from './device-key';
import { clearDeviceRefreshCredential, storeDeviceRefreshCredential } from './device-session-refresh';
import { onDeviceSessionCheckRequested } from './device-session-signal';
import { VAULT_KEYS, type TokenVault } from './native-token-vault';
import { errorCodeOf, rawPost, type RawPost } from './raw-http';
import { handleTerminalAuth, terminalAuthCode } from './terminal-auth';

export const DEVICE_REGISTER_PATH = '/auth/device/register';
/** After a failed attempt, automatic triggers wait this long (explicit ones do not). */
export const REGISTER_RETRY_BACKOFF_MS = 60_000;
/** Server refusals of a device key that a key-less registration still gets past. */
const DEVICE_KEY_REFUSALS = new Set(['DEVICE_KEY_PROOF_INVALID', 'DEVICE_KEY_INVALID']);

export type DeviceSessionNeed = 'none' | 'register' | 'switch_tenant';

interface TokenClaims {
  id?: unknown;
  sid?: unknown;
  tenant_id?: unknown;
  memberships?: unknown;
  impersonation?: unknown;
}

/**
 * What the current token needs:
 *   'register'       no `sid`: a sign-in token not yet bound to this device
 *   'switch_tenant'  bound, but to another tenant than the active one
 *   'none'           bound to the active tenant (or nothing can be done)
 */
export function deviceSessionNeed(token: string | null, activeTenantId: string | null | undefined): DeviceSessionNeed {
  if (!token) return 'none';
  const claims = decodeJwtPayload(token) as TokenClaims | null;
  if (!claims) return 'none';
  // The server refuses to bind an impersonation token; never try.
  if (claims.impersonation === true) return 'none';
  const sid = typeof claims.sid === 'string' && claims.sid.length > 0 ? claims.sid : null;
  const tokenTenant = typeof claims.tenant_id === 'string' ? claims.tenant_id : null;
  if (!sid) {
    // No tenant at all (e.g. a platform admin with no membership): nothing to register in.
    return tokenTenant || activeTenantId ? 'register' : 'none';
  }
  if (activeTenantId && tokenTenant !== activeTenantId) return 'switch_tenant';
  return 'none';
}

export function membershipCount(token: string): number {
  const claims = decodeJwtPayload(token) as TokenClaims | null;
  return Array.isArray(claims?.memberships) ? claims!.memberships.length : 0;
}

export type EnsureOutcome =
  | 'not_native'
  | 'signed_out'
  | 'ok'
  | 'registered'
  | 'no_parent'
  | 'backoff'
  | 'failed'
  | 'terminal'
  | 'stale';

export interface DeviceSessionDeps {
  post: RawPost;
  vault: () => Promise<TokenVault | null>;
  appVersion: () => Promise<string>;
  deviceLabel: () => Promise<string | undefined>;
  platform: () => 'android' | 'ios' | 'other';
  isNative: () => boolean;
  now: () => number;
  /** This install's device-key signer (Android only); absent or null = register unbound. */
  deviceKey?: () => DeviceKeySigner | null;
}

const defaultDeps: DeviceSessionDeps = {
  post: rawPost,
  vault: getSessionTokenVault,
  appVersion: async () => {
    try {
      const info = await CapacitorApp.getInfo();
      if (info.version) return `${info.version}${info.build ? ` (${info.build})` : ''}`.slice(0, 64);
    } catch {
      // fall through
    }
    return 'unknown';
  },
  deviceLabel: async () => {
    try {
      const info = await Device.getInfo();
      const label = [info.manufacturer, info.model].filter(Boolean).join(' ').trim();
      return label ? label.slice(0, 100) : undefined;
    } catch {
      return undefined;
    }
  },
  platform: () => {
    const p = Capacitor.getPlatform();
    return p === 'android' || p === 'ios' ? p : 'other';
  },
  isNative: () => Capacitor.isNativePlatform(),
  now: () => Date.now(),
  deviceKey: getNativeDeviceKeySigner,
};

export class DeviceSessionService {
  private inFlight: Promise<EnsureOutcome> | null = null;
  private lastFailureAt = 0;
  private unsubscribers: Array<() => void> = [];

  constructor(private deps: DeviceSessionDeps = defaultDeps) {}

  /**
   * Make sure the token in use is bound to this device in the active tenant.
   * Single-flight; never throws. `explicit` skips the failure backoff.
   */
  ensure(options: { explicit?: boolean } = {}): Promise<EnsureOutcome> {
    if (this.inFlight) return this.inFlight;
    this.inFlight = this.run(options).finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  private async run(options: { explicit?: boolean }): Promise<EnsureOutcome> {
    if (!this.deps.isNative()) return 'not_native';
    const auth = useAuthStore.getState();
    const token = auth.token;
    if (!auth.isAuthenticated || !token) return 'signed_out';
    const activeTenantId = useTenantStore.getState().activeTenant?.id ?? null;

    const need = deviceSessionNeed(token, activeTenantId);
    if (need === 'none') return 'ok';
    if (!options.explicit && this.deps.now() - this.lastFailureAt < REGISTER_RETRY_BACKOFF_MS) return 'backoff';

    const vault = await this.deps.vault().catch(() => null);
    let credential: string | null = token;
    if (need === 'switch_tenant') {
      credential = vault ? await vault.get(VAULT_KEYS.parentToken).catch(() => null) : null;
      if (!credential) return 'no_parent';
    }

    const headers: Record<string, string> = { Authorization: `Bearer ${credential}` };
    if (activeTenantId) headers['X-Tenant-ID'] = activeTenantId;
    const label = await this.deps.deviceLabel();
    const body = {
      platform: this.deps.platform(),
      client_kind: 'bridge' as const,
      app_version: (await this.deps.appVersion()) || 'unknown',
      ...(label ? { device_label: label } : {}),
    };

    // The proof binds the key to the user and the tenant the server resolves:
    // X-Tenant-ID when sent, the token's own tenant otherwise.
    const claims = decodeJwtPayload(credential) as TokenClaims | null;
    const userId = typeof claims?.id === 'string' ? claims.id : null;
    const proofTenant = activeTenantId ?? (typeof claims?.tenant_id === 'string' ? claims.tenant_id : null);
    const binding =
      userId && proofTenant
        ? await signRegistration(this.deps.deviceKey?.() ?? null, { userId, tenantId: proofTenant, now: this.deps.now() })
        : null;

    let res;
    let keyBound = binding !== null;
    try {
      res = await this.deps.post(
        DEVICE_REGISTER_PATH,
        binding ? { ...body, device_key: binding.deviceKey } : body,
        headers,
        { timeoutMs: 15_000 }
      );
      if (keyBound && res.status === 400 && DEVICE_KEY_REFUSALS.has(errorCodeOf(res.data) ?? '')) {
        // The key is an added protection, never a lock-out: register unbound.
        keyBound = false;
        res = await this.deps.post(DEVICE_REGISTER_PATH, body, headers, { timeoutMs: 15_000 });
      }
    } catch {
      this.lastFailureAt = this.deps.now();
      return 'failed';
    }

    const terminal = terminalAuthCode(res.status, res.data);
    if (terminal) {
      // Only the credential in use speaks for the session. A dead parent
      // (switch_tenant) just means this install cannot join another tenant.
      if (need === 'register') {
        await handleTerminalAuth(terminal);
        return 'terminal';
      }
      if (vault) await vault.remove(VAULT_KEYS.parentToken).catch(() => undefined);
      this.lastFailureAt = this.deps.now();
      return 'failed';
    }

    const answer = res.data as {
      token?: unknown;
      refresh_token?: unknown;
      device_session?: { device_key_id?: unknown } | null;
    } | null;
    const bound = answer?.token;
    if (res.status !== 201 || typeof bound !== 'string' || bound.length === 0) {
      this.lastFailureAt = this.deps.now();
      return 'failed';
    }

    // Signed out, or a different sign-in, while the request was in flight.
    if (useAuthStore.getState().token !== token) return 'stale';

    if (vault) {
      try {
        if (membershipCount(credential) > 1) {
          await vault.set(VAULT_KEYS.parentToken, credential);
        } else {
          await vault.remove(VAULT_KEYS.parentToken);
        }
      } catch {
        // The bound token still works; only a later tenant switch needs the parent.
      }
    }
    if (vault) {
      // Stored before the bound token is used, so its refresh can find it.
      try {
        if (typeof answer?.refresh_token === 'string' && answer.refresh_token.length > 0) {
          const serverKeyId = answer.device_session?.device_key_id;
          await storeDeviceRefreshCredential(vault, {
            refreshToken: answer.refresh_token,
            // The server's id of the key it registered; a 201 to a request that
            // carried a verified key is key-bound even if the answer omits it.
            deviceKeyId: typeof serverKeyId === 'string' && serverKeyId ? serverKeyId : keyBound ? binding!.keyId : null,
          });
        } else {
          await clearDeviceRefreshCredential(vault); // an older server: nothing to renew with
        }
      } catch {
        // The bound token works until it expires; the next registration stores again.
      }
    }
    this.lastFailureAt = 0;
    useAuthStore.getState().setToken(bound);
    return 'registered';
  }

  private scheduled: ReturnType<typeof setTimeout> | null = null;

  /**
   * Run ensure() on the next tick. Sign-in sets the token and the user in two
   * separate store updates (Login.tsx: setToken, then setUser); checking in
   * between would see a token without a signed-in user and do nothing.
   */
  private schedule(explicit: boolean): void {
    if (this.scheduled) return;
    this.scheduled = setTimeout(() => {
      this.scheduled = null;
      void this.ensure({ explicit });
    }, 0);
  }

  /** Follow sign-in, tenant switches and API hints. Native only; idempotent. */
  start(): void {
    if (this.unsubscribers.length > 0 || !this.deps.isNative()) return;
    this.unsubscribers.push(
      useAuthStore.subscribe((state, prev) => {
        const changed = state.token !== prev.token || state.isAuthenticated !== prev.isAuthenticated;
        if (changed && state.token && state.isAuthenticated) this.schedule(true);
      }),
      useTenantStore.subscribe((state, prev) => {
        if (state.activeTenant?.id !== prev.activeTenant?.id) this.schedule(true);
      }),
      onDeviceSessionCheckRequested(() => this.schedule(false))
    );
    this.schedule(true);
  }

  /** For tests. */
  stop(): void {
    this.unsubscribers.splice(0).forEach((u) => u());
    if (this.scheduled) clearTimeout(this.scheduled);
    this.scheduled = null;
    this.lastFailureAt = 0;
  }
}

export const deviceSessionService = new DeviceSessionService();
